import { format } from 'date-fns';
import { parseTimeSlotStartHour, getCourtLabel, getCourtRate, isAddOnCourt, getCourtEnvLabel } from './bookingUtils';
import { formatMoney, formatAmount } from './currencyUtil';
import { buildAvailabilityPalette } from './availabilityThemeUtil';
import {
  AVAILABILITY_IMAGE_CELL,
  AVAILABILITY_IMAGE_CELL_LABELS,
  AVAILABILITY_IMAGE_MULTI_DAY_LABELS,
  GRID_STATUS_PARTIAL,
  SLOT_STATUS_CLOSED,
} from '../constants/bookingConst';

// Builds the data behind the shareable availability images (Admin -> Booking
// -> Grid View -> "Generate images"). Everything a template draws is worked
// out here, once, so the templates stay pure layout and can never disagree
// with each other about what is open.

/**
 * A 'yyyy-MM-dd' key as a local Date, parsed by field — new Date(str) reads a
 * bare date as UTC and can land on the previous day.
 * @param {string} dateStr
 * @returns {Date}
 */
const dateFromKey = (dateStr) => {
  const [year, month, day] = String(dateStr || '').split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

/**
 * Collapses an admin-grid status into what a guest needs to know from an
 * image: can this hour be booked or not.
 *
 * Partial and Processing both read as Booked — a guest cannot take either,
 * and "Processing" means nothing to someone scrolling Facebook. An unknown
 * status also reads as Booked: advertising an hour as free that turns out not
 * to be is worse than under-selling one.
 *
 * @param {string} gridStatus - a computeOriginalStatus() value
 * @returns {string} one of AVAILABILITY_IMAGE_CELL
 */
export const toImageCellStatus = (gridStatus) => {
  switch (gridStatus) {
    case 'open': return AVAILABILITY_IMAGE_CELL.OPEN;
    case 'openPlay': return AVAILABILITY_IMAGE_CELL.OPEN_PLAY;
    case 'passed': return AVAILABILITY_IMAGE_CELL.PASSED;
    case SLOT_STATUS_CLOSED: return AVAILABILITY_IMAGE_CELL.CLOSED;
    case 'booked':
    case GRID_STATUS_PARTIAL:
    case 'processing':
    default:
      return AVAILABILITY_IMAGE_CELL.BOOKED;
  }
};

/**
 * One end of a time range the way the guest booking grid prints it: the
 * hour ("3:00") and its meridiem, with noon and midnight as the app's own
 * NN / MN (see formatTimeSlotRangeParts in bookingUtils.js).
 * @param {number} hour - any integer hour (may be >= 24 past midnight)
 * @returns {{time: string, meridiem: string}}
 */
const hourParts = (hour) => {
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return { time: '12:00', meridiem: 'MN' };
  if (h === 12) return { time: '12:00', meridiem: 'NN' };
  return h < 12 ? { time: `${h}:00`, meridiem: 'AM' } : { time: `${h - 12}:00`, meridiem: 'PM' };
};

/**
 * A row's time range split into its parts — "3:00 – 4:00" over "PM  PM" —
 * so the image's time column matches the guest booking grid, which sets
 * each meridiem under its own number. Works for a merged range too
 * ("12:00 – 6:00" / "NN  PM").
 * @param {number} startHour - absolute start hour
 * @param {number} endHour - absolute end hour
 * @returns {{start: string, startMeridiem: string, end: string, endMeridiem: string}}
 */
export const formatHourRangeParts = (startHour, endHour) => {
  const start = hourParts(startHour);
  const end = hourParts(endHour);
  return { start: start.time, startMeridiem: start.meridiem, end: end.time, endMeridiem: end.meridiem };
};

/**
 * One court's hourly price on this date as a label: "₱350/hr" or
 * "₱350–500/hr" when peak and off-peak differ. Promo prices count, since
 * getCourtRate applies them and the image is for that specific day.
 * @param {object} config
 * @param {number} court
 * @param {Array<{slot: string, dateStr: string}>} rows - the hours the court is open
 * @returns {string} '' when the court has no priced hour
 */
const buildRateLabel = (config, court, rows) => {
  const rates = rows
    .map(({ slot, dateStr }) => Number(getCourtRate(config, court, slot, null, dateStr)))
    .filter(rate => Number.isFinite(rate) && rate > 0);
  if (rates.length === 0) return '';
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  return min === max
    ? `${formatMoney(min, config)}/hr`
    : `${formatMoney(min, config)}–${formatAmount(max)}/hr`;
};

/**
 * Splits a club name into the small "THE" line and the main line the brand
 * mark sets in large type ("The Pickle Pad" -> THE / Pickle Pad). A name
 * without a leading "The" has no prefix.
 * @param {string} name
 * @returns {{prefix: string, main: string}}
 */
export const splitClubName = (name) => {
  const match = (name || '').trim().match(/^(the)\s+(.+)$/i);
  return match ? { prefix: match[1], main: match[2] } : { prefix: '', main: (name || '').trim() };
};

/**
 * Collapses consecutive grid rows whose cells are identical on every court
 * (status, promo and promo price) into one row covering the whole stretch
 * ("12:00 – 6:00 PM"). Used when a day has too many hours for one row per
 * hour to stay readable — twenty hourly rows typically become five or six.
 * @param {Array<object>} rows - snapshot rows, in grid order
 * @param {number[]} courts - court numbers on the image
 * @returns {Array<object>} rows of the same shape as snapshot.rows
 */
export const mergeGridRows = (rows = [], courts = []) => {
  const signatureOf = (row) => courts
    .map((court) => {
      const cell = row.cells[court] || {};
      return `${cell.status}:${cell.promo ? cell.price : ''}`;
    })
    .join('|');
  const merged = [];
  rows.forEach((row) => {
    const signature = signatureOf(row);
    const last = merged[merged.length - 1];
    // A next-day row never joins a same-day one: it carries its own date line.
    if (last && last.signature === signature && last.endHour === row.startHour && last.isNextDay === row.isNextDay) {
      last.endHour += 1;
      return;
    }
    merged.push({ ...row, signature, endHour: row.startHour + 1 });
  });
  return merged.map(({ signature, ...row }) => ({
    ...row,
    timeParts: formatHourRangeParts(row.startHour, row.endHour),
  }));
};

/**
 * Freezes one date's availability into the plain object every availability
 * image template renders from.
 *
 * The caller supplies `resolveCell` rather than this function reading
 * bookings itself, so the image uses exactly the status logic the admin grid
 * already shows (bookings, overrides, Open Play, closed and passed hours) —
 * a second copy of that logic would drift.
 *
 * @param {object} params
 * @param {object} params.config - club config
 * @param {string} params.dateStr - the selected date, 'yyyy-MM-dd'
 * @param {Array<{slot: string, dateStr: string, dayOffset: number}>} params.rows - getGridRowsForDate() output
 * @param {number[]} params.courts - court numbers on screen (add-on columns are dropped here)
 * @param {(rowDate: string, timeSlot: string, court: number) => {status: string, locked?: boolean, promo?: boolean, passed?: boolean}} params.resolveCell
 *   `passed` is whether the hour is over by the clock — booked or overridden
 *   cells keep their own status after their hour, so the status alone can't
 *   tell. Passed hours are left off the snapshot.
 * @param {string} [params.sportName] - shown in the header
 * @param {string} [params.siteHost] - booking site host for the footer, e.g. 'tjopickleyard.com'
 * @returns {object} the snapshot
 * @throws {Error} when no date is given
 */
export const buildAvailabilitySnapshot = ({
  config,
  dateStr,
  rows = [],
  courts = [],
  resolveCell,
  sportName = '',
  siteHost = '',
}) => {
  if (!dateStr) throw new Error('buildAvailabilitySnapshot: a date is required.');
  if (typeof resolveCell !== 'function') throw new Error('buildAvailabilitySnapshot: resolveCell must be a function.');

  // An add-on column (ball machine, coach) is an extra attached to a court
  // hour, not a court a guest can book on its own — it has no place on a
  // "courts available" post.
  const imageCourts = (courts || []).filter(court => !isAddOnCourt(config, court));

  const allRows = (rows || []).map(({ slot, dateStr: rowDate, dayOffset = 0 }) => {
    // Absolute hour, so the after-midnight tail sorts and merges after
    // 11 PM instead of wrapping back to 0.
    const startHour = parseTimeSlotStartHour(slot) + 24 * (dayOffset || 0);
    const cells = {};
    imageCourts.forEach((court) => {
      const resolved = resolveCell(rowDate, slot, court) || {};
      // A locked cell is an hour the court doesn't run at all (switched off,
      // or outside a court-scoped time-slot set). It reads Closed, and it is
      // left out of the "x of y" total — it was never on offer. So is an hour
      // that has already passed: "of y" should count what is still bookable.
      const status = resolved.locked ? AVAILABILITY_IMAGE_CELL.CLOSED : toImageCellStatus(resolved.status);
      const promo = status === AVAILABILITY_IMAGE_CELL.OPEN && Boolean(resolved.promo);
      cells[court] = {
        status,
        promo,
        locked: Boolean(resolved.locked),
        passed: Boolean(resolved.passed) || status === AVAILABILITY_IMAGE_CELL.PASSED,
        // The promo price, shown on the cell the way the guest grid shows it.
        price: promo ? formatMoney(getCourtRate(config, court, slot, null, rowDate), config) : '',
      };
    });
    return {
      key: `${rowDate}|${slot}`,
      slot,
      dateStr: rowDate,
      startHour,
      isNextDay: (dayOffset || 0) > 0,
      timeParts: formatHourRangeParts(startHour, startHour + 1),
      cells,
    };
  });

  // Hours that are already over are dropped so the hours still on offer get
  // the space. Passing is by the clock, so it holds for the whole row; a row
  // is dropped once any court reports it passed. A closed hour later in the
  // day isn't passed and stays.
  const isRowPassed = row => Object.values(row.cells).some(cell => cell.passed);
  const snapshotRows = allRows.filter(row => !isRowPassed(row));

  // "x of y open": y counts the hours still on offer, so switched-off
  // (locked) cells are left out — they were never bookable.
  let openCount = 0;
  let totalCount = 0;
  let hasPromo = false;
  snapshotRows.forEach((row) => {
    Object.values(row.cells).forEach((cell) => {
      if (!cell.locked) totalCount += 1;
      if (cell.status === AVAILABILITY_IMAGE_CELL.OPEN) openCount += 1;
      if (cell.promo) hasPromo = true;
    });
  });

  const courtEntries = imageCourts.map((court) => {
    const runningRows = snapshotRows.filter(row => !row.cells[court]?.locked);
    return {
      number: court,
      label: getCourtLabel(config, court),
      rateLabel: buildRateLabel(config, court, runningRows),
      // 'indoor' / 'outdoor' / '' — printed "(indoor)" under the name, as on the guest grid.
      envLabel: getCourtEnvLabel(config, court),
    };
  });

  const date = dateFromKey(dateStr);
  const nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  const clubName = (config?.courtName || '').trim() || 'Our Courts';
  const nameParts = splitClubName(clubName);

  return {
    dateStr,
    dateLong: format(date, 'EEEE, MMMM d, yyyy'),
    weekdayUpper: format(date, 'EEEE').toUpperCase(),
    dateCompactUpper: format(date, 'MMM d, yyyy').toUpperCase(),
    // "Fri, Oct 2" — under every after-midnight row, as on the guest grid.
    nextDayLabel: format(nextDate, 'EEE, MMM d'),
    clubName,
    clubNamePrefix: nameParts.prefix,
    clubNameMain: nameParts.main,
    logoUrl: config?.logoUrl || '',
    sportName: (sportName || '').trim(),
    siteHost: (siteHost || '').replace(/^www\./i, ''),
    courts: courtEntries,
    rows: snapshotRows,
    mergedRows: mergeGridRows(snapshotRows, imageCourts),
    // Start hours of the rows dropped as passed — a multi-day image shows
    // those hours as Passed in this date's column (buildMultiDaySnapshot).
    passedHours: allRows.filter(isRowPassed).map(row => row.startHour),
    openCount,
    totalCount,
    hasPromo,
    hasRateRange: courtEntries.some(c => c.rateLabel.includes('–')),
    // The club's theme colours (Config -> Theme), see buildAvailabilityPalette.
    palette: buildAvailabilityPalette(config),
  };
};

/**
 * Joins several dates' snapshots into the one a multi-day image renders: one
 * row per hour, and one column per date AND court (Mon · Court 1, Mon ·
 * Court 2, … Sun · Court 3), like a week-at-a-glance timetable.
 *
 * The result has the same row/column shape as a single-day snapshot —
 * `courts` are the date×court columns, each row's `cells` keyed by the
 * column key — so mergeGridRows and planGridRows size and merge it exactly
 * as they do one day. An hour a date doesn't offer reads Closed in that
 * date's column; an hour already over today reads Passed.
 *
 * @param {object[]} snapshots - buildAvailabilitySnapshot() outputs, one per date, in date order
 * @returns {object|null} null when there are no snapshots
 */
export const buildMultiDaySnapshot = (snapshots = []) => {
  const days = (snapshots || []).filter(Boolean);
  if (days.length === 0) return null;
  const first = days[0];
  const last = days[days.length - 1];

  // Columns: every date's courts, date by date.
  const columns = days.flatMap((day, dayIndex) => day.courts.map(court => ({
    number: `${day.dateStr}|${court.number}`,
    court: court.number,
    label: court.label,
    envLabel: court.envLabel,
    rateLabel: court.rateLabel,
    dateStr: day.dateStr,
    dayIndex,
  })));

  // Rows: the union of every date's hours, by absolute hour (an after-
  // midnight row is 24+), so a club that shuts earlier at weekends still
  // gets its weekday evening rows.
  const hours = new Set();
  days.forEach((day) => {
    day.rows.forEach(row => hours.add(row.startHour));
    (day.passedHours || []).forEach(hour => hours.add(hour));
  });
  const rowsByDay = days.map(day => new Map(day.rows.map(row => [row.startHour, row])));
  const passedByDay = days.map(day => new Set(day.passedHours || []));

  const allRows = [...hours].sort((a, b) => a - b).map((startHour) => {
    const cells = {};
    days.forEach((day, dayIndex) => {
      const row = rowsByDay[dayIndex].get(startHour);
      day.courts.forEach((court) => {
        const key = `${day.dateStr}|${court.number}`;
        if (row?.cells?.[court.number]) cells[key] = row.cells[court.number];
        else if (passedByDay[dayIndex].has(startHour)) cells[key] = { status: AVAILABILITY_IMAGE_CELL.PASSED, promo: false, locked: false, passed: true, price: '' };
        else cells[key] = { status: AVAILABILITY_IMAGE_CELL.CLOSED, promo: false, locked: true, passed: false, price: '' };
      });
    });
    return {
      key: `h${startHour}`,
      slot: null,
      dateStr: null,
      startHour,
      isNextDay: startHour >= 24,
      timeParts: formatHourRangeParts(startHour, startHour + 1),
      cells,
    };
  });
  // An hour that has passed on every date in range (today-only range, late
  // in the day) is no use to anyone, so it goes — same as a single day.
  const rows = allRows.filter(row => !Object.values(row.cells).every(cell => cell.passed));

  const openCount = days.reduce((sum, day) => sum + day.openCount, 0);
  const totalCount = days.reduce((sum, day) => sum + day.totalCount, 0);
  const startDate = dateFromKey(first.dateStr);
  const endDate = dateFromKey(last.dateStr);
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();
  // "OCT 12 – 18, 2026" / "OCT 28 – NOV 3, 2026" / "DEC 29, 2026 – JAN 4, 2027"
  let rangeCompact;
  if (sameMonth) rangeCompact = `${format(startDate, 'MMM d')} – ${format(endDate, 'd, yyyy')}`;
  else if (sameYear) rangeCompact = `${format(startDate, 'MMM d')} – ${format(endDate, 'MMM d, yyyy')}`;
  else rangeCompact = `${format(startDate, 'MMM d, yyyy')} – ${format(endDate, 'MMM d, yyyy')}`;

  return {
    ...first,
    isMultiDay: true,
    // Used in the download file name and the audit entry: "2026-10-12-to-2026-10-18".
    dateStr: `${first.dateStr}-to-${last.dateStr}`,
    startDateStr: first.dateStr,
    endDateStr: last.dateStr,
    dateLong: `${format(startDate, 'EEE, MMM d')} – ${format(endDate, 'EEE, MMM d, yyyy')}`,
    weekdayUpper: `${format(startDate, 'EEE')} – ${format(endDate, 'EEE')}`.toUpperCase(),
    dateCompactUpper: rangeCompact.toUpperCase(),
    title: days.length === 7 ? AVAILABILITY_IMAGE_MULTI_DAY_LABELS.WEEK_TITLE : AVAILABILITY_IMAGE_MULTI_DAY_LABELS.TITLE,
    nextDayLabel: AVAILABILITY_IMAGE_MULTI_DAY_LABELS.NEXT_DAY,
    days: days.map((day, dayIndex) => {
      const date = dateFromKey(day.dateStr);
      return {
        dateStr: day.dateStr,
        dayIndex,
        weekdayUpper: format(date, 'EEEE').toUpperCase(),
        weekdayShortUpper: format(date, 'EEE').toUpperCase(),
        dayNumber: format(date, 'd'),
        monthShortUpper: format(date, 'MMM').toUpperCase(),
        courtCount: day.courts.length,
      };
    }),
    courts: columns,
    rows,
    mergedRows: mergeGridRows(rows, columns.map(column => column.number)),
    openCount,
    totalCount,
    hasPromo: days.some(day => day.hasPromo),
    hasRateRange: days.some(day => day.hasRateRange),
  };
};

// The guest booking grid's own proportions (TimeSlotSelection.css): a 42px
// cell with 7px between rows, columns at least ~80px wide (enough for
// "OPEN PLAY"), and court headers that need ~72px. The image table's sizes
// are multiples of a scale `unit` where 1 is exactly the guest screen.
const GUEST_ROW_PITCH = 42 + 7;
const GUEST_MIN_CELL_WIDTH = 80;
const GUEST_MIN_HEADER_WIDTH = 72;
// The time column ("10:00 – 11:00" plus padding) and the column gap, at unit 1.
const GUEST_TIME_COLUMN_WIDTH = 105;
const GUEST_COLUMN_GAP = 8;
// How much larger than the guest page the images set their text, inside the
// same cells. A shared picture is seen smaller than the live page, and the
// guest proportions leave room to spare: a one-word cell uses a quarter of
// its height. 1.5 is the most a two-line Open Play cell still holds, 1.3 the
// most a three-line after-midnight time label does.
// Default enlargement of the cell words and time labels past the guest
// page's proportions, used while the admin leaves the font sizes automatic.
const CELL_TEXT_BOOST = 2;
const TIME_TEXT_BOOST = 1.6;
// Row height a time label needs per unit of its scale (12.2px base, with
// line spacing): two lines (time, meridiems), or three on an after-midnight
// row, which also carries the date. Those rows get their own, smaller scale
// so they don't hold every other row's time down.
const TIME_LABEL_PX_PER_UNIT = 12.2 * 2.5;
const TIME_LABEL_NEXT_DAY_PX_PER_UNIT = 12.2 * 3.5;
// Height a cell's words need per unit of cell-text scale (9.9px base,
// line-height 1.2): one line, or two for Open Play's "(Join online)" —
// Open Play gets its own scale for the same reason.
const CELL_TEXT_PX_PER_UNIT = 9.9 * 1.2;
// Font sizes at scale 1 (guest page): time labels and cell words. A px size
// the admin types is divided by these to get the scale it stands for.
const TIME_TEXT_BASE_PX = 12.2;
const CELL_TEXT_BASE_PX = 9.9;
const CELL_TEXT_OPEN_PLAY_PX_PER_UNIT = (9.9 + 8.8) * 1.2 + 2;
// Share of a cell's height its words may fill.
const CELL_TEXT_FILL = 0.72;
// Width (scale 1) the longest one-word status ("BOOKED") and "OPEN PLAY"
// take, not counting the cell's side padding (6px each side, × cellUnit).
const CELL_WORD_WIDTH = 46;
const CELL_OPEN_PLAY_WIDTH = 68;
const CELL_SIDE_PADDING = 12;
// The time labels never get more than this much bigger than the cell words,
// so on a many-court day the time column doesn't take the courts' width.
const TIME_TO_CELL_MAX = 1.5;
// Nor does the time column ever take more than this share of the table.
const TIME_COLUMN_MAX_SHARE = 0.34;

/**
 * Chooses the rows for a full-grid image: one per hour when they come out
 * big enough to read, otherwise snapshot.mergedRows (hours with the same
 * availability on every court joined into one range). Also works out the
 * row height and the type scale for whichever was chosen.
 *
 * @param {object} snapshot - buildAvailabilitySnapshot() output
 * @param {object} sizing
 * @param {number} sizing.bodyHeight - px the rows may take in total
 * @param {number} sizing.minHourlyUnit - smallest scale (1 = guest-page size) at which hourly rows are kept
 * @param {'ranges'|'hourly'} [sizing.rowMode] - 'hourly' always keeps one row per hour, however small
 * @param {number} sizing.maxUnit - largest scale the type may grow to
 * @param {number} sizing.maxRowHeight - px; rows stop growing here on a short day
 * @param {number} sizing.tableWidth - px across the whole table
 * @param {number} sizing.maxHeaderUnit - largest scale for the court header row
 * @param {{time?: number|null, cell?: number|null}} [sizing.textPx] - the admin's font sizes in px (Availability
 *   images settings) for the time labels and the one-word cell labels. Used exactly as typed; null/absent means
 *   automatic, which fits the text inside its row and column. Open Play's two-line words stay fitted to the cell.
 * @returns {{rows: object[], merged: boolean, rowHeight: number, unit: number, cellUnit: number, headerUnit: number,
 *   cellTextBoost: number, openPlayTextBoost: number, timeTextBoost: number, nextDayTimeTextBoost: number}}
 *   `unit` sizes the rows and time column; `cellUnit` the words inside the
 *   cells and `headerUnit` the court names, both held down when many courts
 *   make the columns too narrow for the row-sized text. `cellTextBoost` and
 *   `timeTextBoost` enlarge the cell words and time labels beyond the guest
 *   page's proportions without changing the cell sizes; Open Play cells and
 *   after-midnight time labels have their own (smaller) boosts.
 */
export const planGridRows = (snapshot, {
  bodyHeight, minHourlyUnit, maxUnit, maxRowHeight, tableWidth, maxHeaderUnit, rowMode = 'ranges', textPx = {},
}) => {
  const timePx = Number(textPx?.time) > 0 ? Number(textPx.time) : null;
  const cellPx = Number(textPx?.cell) > 0 ? Number(textPx.cell) : null;
  const fit = (count) => Math.min(maxRowHeight, Math.floor(bodyHeight / Math.max(count, 1)));
  const hourlyHeight = fit(snapshot.rows.length);
  const merged = rowMode !== 'hourly'
    && hourlyHeight / GUEST_ROW_PITCH < minHourlyUnit
    && snapshot.mergedRows.length < snapshot.rows.length;
  const rows = merged ? snapshot.mergedRows : snapshot.rows;
  const rowHeight = merged ? fit(rows.length) : hourlyHeight;
  // Type follows the row but stops at maxUnit, so a quiet day with three
  // tall rows doesn't get billboard-sized text.
  const unit = Math.min(maxUnit, rowHeight / GUEST_ROW_PITCH);
  const courtCount = Math.max(snapshot.courts.length, 1);
  const columnWidthFor = timeScale => (tableWidth - GUEST_TIME_COLUMN_WIDTH * timeScale - GUEST_COLUMN_GAP * unit * courtCount) / courtCount;
  // Time labels: the admin's px size exactly, or automatic — boosted, but
  // kept inside the row height...
  let timeScale = timePx
    ? timePx / TIME_TEXT_BASE_PX
    : Math.min(
      unit * TIME_TEXT_BOOST,
      rowHeight / TIME_LABEL_PX_PER_UNIT,
      (tableWidth * TIME_COLUMN_MAX_SHARE) / GUEST_TIME_COLUMN_WIDTH,
    );
  let columnWidth = columnWidthFor(timeScale);
  let cellUnit = Math.min(unit, columnWidth / GUEST_MIN_CELL_WIDTH);
  // ...and, when many courts squeeze the columns, scaled back towards the
  // cell text so the courts get the width (automatic size only).
  if (!timePx && timeScale > cellUnit * TIME_TO_CELL_MAX) {
    timeScale = Math.max(cellUnit * TIME_TO_CELL_MAX, 1);
    columnWidth = columnWidthFor(timeScale);
    cellUnit = Math.min(unit, columnWidth / GUEST_MIN_CELL_WIDTH);
  }
  // After-midnight rows' three-line labels, shrunk only as far as they must.
  const nextDayTimeScale = Math.min(timeScale, rowHeight / TIME_LABEL_NEXT_DAY_PX_PER_UNIT);
  // Cell words are sized from the row (so a wider time column doesn't
  // shrink them), then fitted across the column and inside the cell's height
  // (the row less its 7px gap). The boost is relative to cellUnit, which
  // scales the cell's padding and corners.
  const cellHeight = rowHeight - 7 * unit;
  const fitCellText = (wordWidth, pxPerUnit) => Math.max(1, Math.min(
    unit * CELL_TEXT_BOOST,
    (columnWidth - CELL_SIDE_PADDING * cellUnit) / wordWidth,
    (cellHeight * CELL_TEXT_FILL) / pxPerUnit,
  ) / cellUnit);
  // A typed px size is used exactly; Open Play's two lines still never
  // outgrow the cell.
  const cellTextBoost = cellPx
    ? cellPx / CELL_TEXT_BASE_PX / cellUnit
    : fitCellText(CELL_WORD_WIDTH, CELL_TEXT_PX_PER_UNIT);
  const openPlayTextBoost = Math.min(cellTextBoost, fitCellText(CELL_OPEN_PLAY_WIDTH, CELL_TEXT_OPEN_PLAY_PX_PER_UNIT));
  // Headers track the rows (a little larger, as on the guest page) but never
  // outgrow their column.
  const headerUnit = Math.min(maxHeaderUnit, columnWidth / GUEST_MIN_HEADER_WIDTH, Math.max(1, unit * 1.3));
  return {
    rows,
    merged,
    rowHeight,
    unit,
    cellUnit,
    headerUnit,
    cellTextBoost,
    openPlayTextBoost,
    timeTextBoost: timeScale / unit,
    nextDayTimeTextBoost: nextDayTimeScale / unit,
  };
};

// Compact multi-day table (phone sizes): how wide a time label runs per px
// of its font ("10:00 – 11:00" plus padding), and the size limits of the
// text it draws. Font px here are image pixels.
const COMPACT_TIME_EM_WIDTH = 7.2;
const COMPACT_TIME_PX = { min: 14, max: 22 };
const COMPACT_WORD_PX = { min: 8, max: 30 };
const COMPACT_DAY_PX = { min: 14, max: 30 };
// Gaps inside a day's cell: between court slots, around them, and between rows.
const COMPACT_SLOT_GAP = 3;
const COMPACT_CELL_PAD = 2;
const COMPACT_ROW_GAP = 4;
// The court-name row under the dates, only drawn when courts sit side by side.
const COMPACT_COURT_HEADER_HEIGHT = 36;
// Width of the status words per px of font: bold caps ≈ .68em a letter, plus
// the slot's side padding.
const COMPACT_LETTER_EM = 0.68;
const COMPACT_WORD_PAD_EM = 0.9;
// The court-number column beside the time labels, only drawn when courts
// are stacked (each stacked slot lines up with its number there).
const COMPACT_COURT_GUTTER_WIDTH = 34;

/**
 * Keeps a number between two limits.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Sizes the compact multi-day table on the phone images: hours down the
 * side, one column per DATE, and inside each cell one slot per court with
 * its status word (OPEN / BOOKED / OPEN PLAY / CLOSED), as on the guest grid.
 *
 * A week of courts doesn't fit across a phone as full cells, so the courts
 * of a date share its column one of two ways, whichever gives the bigger
 * words: SIDE BY SIDE (court names on a row under the dates; best for a few
 * dates) or STACKED in the cell, lined up with a column of court numbers
 * beside the times (best for a full week). The admin's typed font size (image settings) is used exactly
 * for the words; only the arrangement is still chosen automatically.
 *
 * Rows are hourly while they come out at least `minHourlyRowHeight` tall
 * (or always, with rowMode 'hourly'); otherwise hours with the same
 * availability on every date and court merge into ranges, as on a single day.
 *
 * @param {object} snapshot - buildMultiDaySnapshot() output
 * @param {object} sizing
 * @param {number} sizing.bodyHeight - px the hour rows may take in total
 * @param {number} sizing.tableWidth - px across the whole table
 * @param {number} sizing.minHourlyRowHeight - px; shorter hourly rows merge into ranges
 * @param {number} sizing.maxRowHeight - px; rows stop growing here
 * @param {'ranges'|'hourly'} [sizing.rowMode]
 * @param {{time?: number|null, cell?: number|null}} [sizing.textPx] - the admin's font sizes (time labels, slot words); automatic when absent
 * @returns {{rows: object[], merged: boolean, rowHeight: number, stacked: boolean, timePx: number, wordPx: number, openPlayPx: number, dayPx: number}}
 */
export const planCompactGridRows = (snapshot, {
  bodyHeight, tableWidth, minHourlyRowHeight, maxRowHeight, rowMode = 'ranges', textPx = {},
}) => {
  const typedTime = Number(textPx?.time) > 0 ? Number(textPx.time) : null;
  const typedCell = Number(textPx?.cell) > 0 ? Number(textPx.cell) : null;
  const dayCount = Math.max(snapshot.days?.length || 1, 1);
  const courts = Math.max(1, ...(snapshot.days || []).map(day => day.courtCount || 0));

  /**
   * Rows, row height and the biggest word size for one arrangement.
   * @param {boolean} stacked
   * @returns {object}
   */
  const planFor = (stacked) => {
    const height = bodyHeight - (stacked ? 0 : COMPACT_COURT_HEADER_HEIGHT);
    const fit = count => Math.min(maxRowHeight, Math.floor(height / Math.max(count, 1)));
    const hourlyHeight = fit(snapshot.rows.length);
    const merged = rowMode !== 'hourly'
      && hourlyHeight < minHourlyRowHeight
      && snapshot.mergedRows.length < snapshot.rows.length;
    const rows = merged ? snapshot.mergedRows : snapshot.rows;
    const rowHeight = merged ? fit(rows.length) : hourlyHeight;
    // Two-line time labels (times over meridiems; three after midnight).
    const timePx = typedTime || clamp(Math.floor(rowHeight * 0.34), COMPACT_TIME_PX.min, COMPACT_TIME_PX.max);
    const gutter = stacked ? COMPACT_COURT_GUTTER_WIDTH : 0;
    const dayWidth = (tableWidth - timePx * COMPACT_TIME_EM_WIDTH - gutter - COMPACT_SLOT_GAP * dayCount) / dayCount;
    const innerWidth = dayWidth - COMPACT_CELL_PAD * 4;
    const innerHeight = rowHeight - COMPACT_ROW_GAP - COMPACT_CELL_PAD * 2;
    const wordEm = AVAILABILITY_IMAGE_CELL_LABELS.booked.length * COMPACT_LETTER_EM + COMPACT_WORD_PAD_EM;
    const openPlayEm = AVAILABILITY_IMAGE_CELL_LABELS.openPlay.length * COMPACT_LETTER_EM + COMPACT_WORD_PAD_EM;
    const slotWidth = stacked ? innerWidth : (innerWidth - COMPACT_SLOT_GAP * (courts - 1)) / courts;
    const slotHeight = stacked ? (innerHeight - COMPACT_SLOT_GAP * (courts - 1)) / courts : innerHeight;
    // Caps are ~.72em tall, so a word can take most of its slot's height.
    const fitPx = Math.min(slotHeight * 0.82, slotWidth / wordEm);
    const wordPx = typedCell || clamp(Math.floor(fitPx), COMPACT_WORD_PX.min, COMPACT_WORD_PX.max);
    return {
      rows, merged, rowHeight, stacked, timePx, wordPx,
      openPlayPx: Math.min(wordPx, Math.max(COMPACT_WORD_PX.min, Math.floor(slotWidth / openPlayEm))),
      fitPx,
      dayPx: clamp(Math.floor(dayWidth * 0.2), COMPACT_DAY_PX.min, COMPACT_DAY_PX.max),
    };
  };

  const sideBySide = planFor(false);
  const stacked = courts > 1 ? planFor(true) : sideBySide;
  const { fitPx, ...plan } = stacked.fitPx > sideBySide.fitPx ? stacked : sideBySide;
  return plan;
};

/**
 * A club-name size that keeps a long name inside the header slot: the
 * design size up to `fitsAt` characters, then stepping down.
 * @param {string} name
 * @param {number} designSize - px, as in the mockup
 * @param {number} fitsAt - longest name (characters) that fits at designSize
 * @returns {number}
 */
export const fitClubNameSize = (name, designSize, fitsAt) => {
  const length = (name || '').length;
  if (length <= fitsAt) return designSize;
  return Math.max(Math.round(designSize * 0.55), Math.floor((designSize * fitsAt) / length));
};

/**
 * File name for a downloaded image: "availability-2026-10-01-full-grid-dark.png".
 * @param {string} dateStr - 'yyyy-MM-dd'
 * @param {string} templateId - camelCase template id
 * @returns {string}
 */
export const buildAvailabilityImageFileName = (dateStr, templateId) => {
  const slug = (templateId || 'image').replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  return `availability-${dateStr || 'date'}-${slug}.png`;
};
